export const DIVE_MS = 2500
export const INTRO_DIVE_MS = 3500
export const INTRO_HOLD_MS = 400
/** Leaflet flyTo uses 0.8·S seconds; clamp so long mosaic hops stay watchable. */
export const FLY_MS_MIN = 2200
export const FLY_MS_MAX = 4800
/** van Wijk / Leaflet flyTo curvature — higher flies higher. */
export const FLY_RHO = 1.42
/**
 * CSS px per slot at the intro stop.
 *
 * Two floors, and the landing must clear both:
 *
 * - `STILL_OFF_PX` (28, in mosaic_hook.js): below it the coarse poster is
 *   still painted over the tiles, so the dive "lands" on the same smooth
 *   artwork it left and the photos never appear. Measured 2026-10-23 at the
 *   old 24 px: still opacity 0.83 against tiles 0.17.
 * - `TAP_MIN_PX` (44, in mosaic_hook.js): below it a photo cannot be opened,
 *   so the landing would show photos that ignore a tap.
 *
 * 48 px satisfies both and reads as a photo grid (8 slots across on a 390 px
 * screen) rather than a wall of colour chips.
 */
export const INTRO_CELL_PX = 48

export function diveEase(t) {
  const x = t < 0 ? 0 : t > 1 ? 1 : t
  return 1 - (1 - x) ** 1.5
}

function sinh(n) {
  return (Math.exp(n) - Math.exp(-n)) / 2
}

function cosh(n) {
  return (Math.exp(n) + Math.exp(-n)) / 2
}

/**
 * Leaflet `Map.flyTo` path (van Wijk 2003). One hyperbolic takeoff-and-landing
 * in (pan, scale) — not zoom-out / cruise / zoom-in stages.
 *
 * `from`/`to` are map-pixel centers at `startZoom` (Leaflet `project` at that zoom).
 */
export function flyPath({
  fromX,
  fromY,
  toX,
  toY,
  startZoom,
  targetZoom,
  sizeX,
  sizeY,
  rho = FLY_RHO,
}) {
  const w0 = Math.max(sizeX, sizeY)
  const w1 = w0 * 2 ** (startZoom - targetZoom)
  const u1 = Math.hypot(toX - fromX, toY - fromY) || 1
  const rho2 = rho * rho

  const r = (i) => {
    const b =
      (w1 * w1 - w0 * w0 + (i ? -1 : 1) * rho2 * rho2 * u1 * u1) /
      (2 * (i ? w1 : w0) * rho2 * u1)
    const t = Math.sqrt(b * b + 1) - b
    return t < 1e-9 ? -18 : Math.log(t)
  }

  const r0 = r(0)
  const S = (r(1) - r0) / rho

  const uAt = (s) => (w0 * (cosh(r0) * Math.tanh(r0 + rho * s) - sinh(r0))) / rho2
  const wAt = (s) => w0 * (cosh(r0) / cosh(r0 + rho * s))

  return {
    S,
    at(t) {
      const s = diveEase(t) * S
      const frac = uAt(s) / u1
      return {
        x: fromX + (toX - fromX) * frac,
        y: fromY + (toY - fromY) * frac,
        zoom: startZoom + Math.log2(w0 / wAt(s)),
      }
    },
  }
}

export function flyDurationMs(S, min = FLY_MS_MIN, max = FLY_MS_MAX) {
  const natural = 1000 * S * 0.8
  return Math.min(max, Math.max(min, natural))
}

export function diveTargetZoom(slotMaxZoom, slotNativeZoom) {
  return Math.min(slotMaxZoom, slotNativeZoom - 1)
}

export function introTargetZoom(slotNativeZoom, tilePx = 256) {
  return slotNativeZoom + Math.log2(INTRO_CELL_PX / tilePx)
}

export function slotNativePx(x, y, tilePx) {
  return {x: (x + 0.5) * tilePx, y: (y + 0.5) * tilePx}
}

export function diveFocus(start, end, u) {
  return {
    x: start.x + (end.x - start.x) * u,
    y: start.y + (end.y - start.y) * u,
  }
}

export function scaleNativePx(nativePx, zoom, nativeZoom) {
  const s = 2 ** (zoom - nativeZoom)
  return {x: nativePx.x * s, y: nativePx.y * s}
}

/** Map-pixel center at `zoom` that puts `slotAtZoom` under container pixel `focus`. */
export function diveCenterAtZoom(slotAtZoom, focus, half) {
  return {
    x: slotAtZoom.x - focus.x + half.x,
    y: slotAtZoom.y - focus.y + half.y,
  }
}

export function containerPointOf(slotAtZoom, center, half) {
  return {
    x: slotAtZoom.x - (center.x - half.x),
    y: slotAtZoom.y - (center.y - half.y),
  }
}
